import { Slider } from '@material-ui/core';
import BlobImage from 'components/blobImage';
import CompetencyRating from 'components/general/competencyRating';
import PanelSlider from 'components/panelSlider';
import { ArrayHelpers, Copy, Forms, Help, StringHelpers, SystemText } from 'e4shared';
import { systemTranslationTypes, translateOverride, translateSys } from 'helpers/translations';
import React from 'react';
import { useSelector } from 'react-redux';
import { CompetencyRoleDescriptions } from 'views/user/components/competencyRoleDescriptions';

export const CalcAutoValue = (user, goals, pi) => {
    if (!pi.goal) return 0;
    const ratingRange = user.companySettings?.display?.ratingRange || 5;
    const ratingArray = new Array(ratingRange).fill(0);
    const goal = goals?.find(g => g.quota_Id === pi.goal);
    const multiplier = pi.period === 'W' ? 4 : pi.period === 'M' ? 3 : 1;
    if (goal) pi.target = pi.metricFormat === 'P' ? goal.value : goal.value * multiplier;
    const auto = pi.metric && pi.period ? (pi.target ? (pi.achieved / pi.target) * 100 : 0) : null;
    const valid = (auto, range) => auto >= range;

    if (pi.metricLow) {
        let lowAutoValue = 1;
        if (auto > pi.ratingGroup.range1) lowAutoValue = 1;
        if (!lowAutoValue && auto > pi.ratingGroup.range2) lowAutoValue = 2;
        if (!lowAutoValue && auto > pi.ratingGroup.range3) lowAutoValue = 3;
        if (!lowAutoValue && auto > pi.ratingGroup.range4) lowAutoValue = 4;
        if (!lowAutoValue && auto > pi.ratingGroup.range5) lowAutoValue = 5;
        return lowAutoValue;
    }

    const autoValue =
        auto !== null ? ratingArray.reduce((a, c, i) => (valid(auto, pi.ratingGroup[`range${i + 1}`]) ? i + 1 : a), 0) : 0;

    return autoValue;
};

export const CalculatePIRating = (rating, predictions) => {
    const answers = predictions[rating.id]?.positiveIndicators;
    const totalWeight = rating.positiveIndicators.reduce((a, c) => c.weighting + a, 0);
    const result = rating.positiveIndicators.reduce((a, c, i) => {
        const ans = answers ? answers[i + 1] : 0;
        const w = c.weighting * (ans || 1);
        return w + a;
    }, 0);

    const level = Math.round(result / totalWeight);

    return level;
};

export const Rating = ({
    profile,
    pages,
    page,
    setPage,
    comment,
    rate,
    state,
    buttons,
    predictions,
    marks,
    usePIs,
    error,
    appraisal,
    next,
    inPreview,
    goals,
    competency,
    setSliding,
    sliding,
    showBothManagerAndStaffRatingInfo,
}) => {
    const [whosComment, setWhosComment] = React.useState('M');

    const { user } = useSelector(state => state.user);
    const { tags, handles } = useSelector(state => state.ui);
    const ratingText = translateSys(
        user.language_Code,
        systemTranslationTypes.ratings,
        user.companySettings,
        SystemText('Admin/Config/Defaults/Ratings')
    );

    const ratingRange = user.companySettings?.display?.ratingRange || 5;
    const ratingArray = new Array(ratingRange).fill(0);

    const hideColourFromUser = user.companySettings?.development?.greyRatingsForUserReviews;
    const expandRatingDescriptions = user.companySettings?.development?.expandRatingDescriptions;

    React.useEffect(() => {
        if (appraisal?.participants === 'U') setWhosComment('U');
    }, [appraisal?.participants]);

    const answer = a => {
        const temp = { ...predictions[competency.id] };

        temp.positiveIndicators[competency.piIndex.toString()] = a;

        temp.level = inPreview ? 1 : CalculatePIRating(competency, predictions);

        rate(competency, temp.level, temp.positiveIndicators);

        setTimeout(() => {
            next();
        }, 250);
    };

    const selected = (i, a, autoValue, userAnswer, managerAnswer) => {
        if (!competency?.id) return false;
        const thesePredictions = predictions[competency.id]?.positiveIndicators;
        if (autoValue) {
            return autoValue === a ? true : false;
        } else {
            const u = userAnswer || (!state.manager && thesePredictions[i.toString()]);
            const m = managerAnswer || (state.manager && thesePredictions[i.toString()]);
            const showUser = a === u && (!state.manager || showBothManagerAndStaffRatingInfo);
            const showManager = a === m && (state.manager || showBothManagerAndStaffRatingInfo);
            return showUser || showManager ? true : false;
        }
    };

    const description = comp => {
        const desc =
            comp.roleDescriptions?.filter(x => x.description).length > 0 ? (
                <CompetencyRoleDescriptions descriptions={comp.roleDescriptions.filter(x => x.description)} />
            ) : comp.description ? (
                <>
                    <Copy value={comp.description} />
                    {/* <div className="mb3">
                        {comp.roles?.map((r, i) => {
                            return (
                                <label key={`rating-roles-${i}`} className="label label-primary mr3">
                                    {r}
                                </label>
                            );
                        })}
                    </div> */}
                </>
            ) : null;

        return desc;
    };

    const ratingComments = localRating => {
        return (
            <>
                <Forms.Textarea
                    data-testid="text-appraisal-comments"
                    key={`comments-${localRating.id}`}
                    rows={state.finalStarted ? 10 : 13}
                    readonly={state.readonly}
                    value={
                        state.finalStarted || appraisal.manager_Guid
                            ? whosComment === 'M'
                                ? predictions[localRating.id]?.managerComments || predictions[localRating.id]?.comments
                                : predictions[localRating.id]?.userComments || predictions[localRating.id]?.comments
                            : predictions[localRating.id]?.comments
                    }
                    onChange={comment}
                    tags={tags}
                    handles={handles}
                    label={SystemText('Reviews/Appraisals/Wizard/Fields/Comment/Label')}
                    mandatory={localRating.review_Comment_Mandatory}
                    placeholder={
                        localRating.review_Comment_Mandatory
                            ? SystemText('Reviews/Appraisals/Wizard/Fields/Comment/Mandatory')
                            : SystemText('Reviews/Appraisals/Wizard/Fields/Comment')
                    }
                />
                {state.finalStarted && (
                    <div className="btn-row">
                        <button
                            className={`btn btn-sm ${whosComment === 'M' ? 'btn-primary' : 'btn-default'}`}
                            onClick={() => setWhosComment('M')}
                        >
                            {SystemText('Reviews/Appraisals/Wizard/Fields/Comment/Manager')}
                        </button>
                        <button
                            className={`btn btn-sm ${whosComment === 'U' ? 'btn-primary' : 'btn-default'}`}
                            onClick={() => setWhosComment('U')}
                        >
                            {SystemText('Reviews/Appraisals/Wizard/Fields/Comment/User')}
                        </button>
                    </div>
                )}
            </>
        );
    };

    const errorPanel = error => {
        return error ? (
            <div
                id="error-anchor"
                style={{ fontWeight: 500, fontSize: '14px' }}
                className="mt10 p20 label label-danger flex flex-center"
            >
                <i className="fa fa-warning mr10" />
                {error}
            </div>
        ) : null;
    };

    const piPanel = p => {
        const pi = p;
        const i = p.piIndex;
        const autoValue = pi.goal ? CalcAutoValue(user, goals, pi) : 0;
        const userAnswer = predictions[competency.id]?.userPositiveIndicators
            ? predictions[competency.id]?.userPositiveIndicators[p.piIndex.toString()]
            : null;

        const managerAnswer = predictions[competency.id]?.managerPositiveIndicators
            ? predictions[competency.id]?.managerPositiveIndicators[p.piIndex.toString()]
            : null;

        return (
            <div className="piStatement" key={`pi-${pi.id}`}>
                <span className="flex-grow piStatementTitle">{pi.statement}</span>
                <div>
                    {ratingArray.map((n, x) => {
                        const r = {};
                        r.value = x + 1;
                        const isSelected = selected(i, r.value, autoValue, userAnswer, managerAnswer);
                        r.name = pi.ratingGroup[`option${x + 1}`];
                        return (
                            <div
                                key={`piRating-${x}-${pi.id}-${r.value}`}
                                className={`flex flex-center piAnswer ${
                                    showBothManagerAndStaffRatingInfo || state.readonly ? 'readonly' : ''
                                } ${isSelected ? 'selected' : ''} ${
                                    pi.metric &&
                                    r.value !== autoValue &&
                                    !showBothManagerAndStaffRatingInfo &&
                                    !state.readonly
                                        ? 'disabled'
                                        : ''
                                }`}
                                data-testid="button-appraisal-pi"
                                onClick={
                                    sliding
                                        ? null
                                        : () =>
                                              (pi.metric && r.value !== autoValue) ||
                                              showBothManagerAndStaffRatingInfo ||
                                              state.readonly
                                                  ? null
                                                  : answer(r.value)
                                }
                            >
                                {isSelected ? (
                                    <i className="fa fa-check mr10 text-success" />
                                ) : (
                                    <i className="fa fa-circle mr10" />
                                )}
                                <div className="flex-grow">{r.name}</div>
                                {showBothManagerAndStaffRatingInfo && (
                                    <>
                                        {userAnswer === r.value && (
                                            <Help
                                                key={`previous-user-${r.value}`}
                                                id={`previous-user-${r.value}`}
                                                target={
                                                    <BlobImage
                                                        height="20"
                                                        folder="avatar"
                                                        initials={profile.initials}
                                                        className="ml5"
                                                        src={profile.avatar}
                                                    />
                                                }
                                            >
                                                {profile.full_Name}
                                            </Help>
                                        )}
                                        {managerAnswer === r.value && (
                                            <Help
                                                key={`previous-manager-${r.value}`}
                                                id={`previous-manager-${r.value}`}
                                                target={
                                                    <BlobImage
                                                        height="20"
                                                        folder="avatar"
                                                        initials={profile.manager_Initials}
                                                        className="ml5"
                                                        src={profile.manager_Avatar}
                                                    />
                                                }
                                            >
                                                {profile.manager_Full_Name}
                                            </Help>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })}

                    <div className="flex">
                        <div className="piTags">
                            {pi.roles?.map((r, i) => (
                                <label key={`role-${i}`} className="label label-primary">
                                    {r}
                                </label>
                            ))}
                        </div>
                        {pi.metric && (
                            <div>
                                <div className="no-wrap mt10">
                                    <i className="fa fa-warning text-warning mr10" />
                                    {SystemText('Reviews/Appraisals/Wizard/Rating/PreCalculated')}
                                    <Help id={`pi-help-${pi.id}`} className="mr5">
                                        {SystemText(
                                            `Reviews/Appraisals/Wizard/Rating/PreCalculated/Who/${
                                                user.id === profile.id ? 'You' : 'Them'
                                            }`,
                                            profile.full_Name
                                        )}
                                        {SystemText('Reviews/Appraisals/Wizard/Rating/PreCalculated/Metric', pi.metricName)}
                                        {SystemText(
                                            `Reviews/Appraisals/Wizard/Rating/PreCalculated/Period/${
                                                pi.period === 'M'
                                                    ? 'Months'
                                                    : pi.period === 'W'
                                                      ? 'Weeks'
                                                      : pi.period === 'Q'
                                                        ? 'Quarters'
                                                        : 'Years'
                                            }`
                                        )}
                                        {SystemText(
                                            `Reviews/Appraisals/Wizard/Rating/PreCalculated/Achieved/${
                                                user.id === profile.id ? 'You' : 'Them'
                                            }`,
                                            StringHelpers.formatMetric(pi.metricFormat, pi.achieved, user.currencySymbol)
                                        )}
                                        {SystemText(
                                            'Reviews/Appraisals/Wizard/Rating/PreCalculated/Target',
                                            StringHelpers.formatMetric(pi.metricFormat, pi.target, user.currencySymbol)
                                        )}
                                        {!showBothManagerAndStaffRatingInfo && appraisal.participants === 'UM' && (
                                            <>
                                                <br />
                                                <br />
                                                {SystemText('Reviews/Appraisals/Wizard/Rating/PreCalculated/Note')}
                                            </>
                                        )}
                                    </Help>
                                </div>
                                {!showBothManagerAndStaffRatingInfo && !state.readonly && (
                                    <button
                                        onClick={() => answer(autoValue)}
                                        data-testid="button-continue"
                                        className="mt10 btn-sm btn btn-warning"
                                        style={{ float: 'right' }}
                                    >
                                        {SystemText('Reviews/Appraisals/Wizard/Buttons/Continue')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {errorPanel(error)}
            </div>
        );
    };

    const commentPanel = (p, showError = true) => {
        return (
            <div className="mt20 text-left" key={`comment-holder-${competency.id}`}>
                {showBothManagerAndStaffRatingInfo ? (
                    <div className="row">
                        <div className="col-md-6" data-testid="text-reviewers-comments">
                            <Forms.Label
                                label={SystemText('Reviews/Appraisals/ManagerComments')}
                                value={predictions[competency.id]?.managerComments}
                            />
                        </div>
                        <div className="col-md-6" data-testid="text-users-comments">
                            <Forms.Label
                                label={SystemText('Reviews/Appraisals/UserComments')}
                                value={predictions[competency.id]?.userComments}
                            />
                        </div>
                    </div>
                ) : (
                    ratingComments(competency)
                )}
                {showError && errorPanel(error)}
            </div>
        );
    };

    const sliderPanel = p => {
        return (
            <div className="flex-grow" key={`slider-holder-${competency.id}`}>
                <div className="m20">
                    <Slider
                        data-testid="ratings-slider"
                        key={`slider-${competency.id}`}
                        value={predictions[competency.id]?.level || 0}
                        min={0}
                        max={ratingRange}
                        marks={marks}
                        step={1}
                        onChange={(a, value) => (inPreview ? next() : state.readonly ? null : rate(competency, value))}
                    />
                </div>
                {p.review_Comment && commentPanel(p, false)}
                {!error && (
                    <div className="mt0 rater howTo">
                        <h4>{SystemText('Reviews/Appraisals/Wizard/Headings/H4/HowToRate')}</h4>
                        <p>
                            <Copy value={ratingText} />
                        </p>
                    </div>
                )}
                {errorPanel(error)}
            </div>
        );
    };

    competency = translateOverride(user.language_Code, competency);
    const competencyDescription = description(competency);

    return (
        <div className="text-left mb10" data-testid="ratings-panel">
            <div className="rater">
                <div className="flex flex-center">
                    <div className="flex flex-top flex-grow" key={`area-${competency.id}`}>
                        <div className="flex-grow">
                            <div className="uppercase flex flex-flex-center">
                                <span style={{ paddingTop: '7px' }}>{competency?.area}</span>
                            </div>
                            <h2 className="flex flex-center mt10">
                                {competency.level > 0 && (
                                    <span
                                        title={SystemText('Reviews/Appraisals/Wizard/Spans/CurrentRating/Title')}
                                        className={`mr10 text-sm`}
                                    >
                                        <CompetencyRating
                                            hideColour={state.user && hideColourFromUser}
                                            hideUnrated
                                            level={competency.level}
                                            badge
                                        />
                                    </span>
                                )}
                                <span className="inline-flex flex-center">
                                    {competency.name}
                                    {!expandRatingDescriptions && competencyDescription && (
                                        <Help id={`help-raing-description`}>{competencyDescription}</Help>
                                    )}
                                </span>
                            </h2>
                        </div>
                        <div className="mt10">{buttons()}</div>
                    </div>
                </div>
                <hr className="mb20 mt10" />
                {expandRatingDescriptions && competencyDescription && <div>{competencyDescription}</div>}
                <PanelSlider
                    showIndex={page - 1}
                    zoomToStart
                    onSlideStart={() => setSliding(true)}
                    onSlideComplete={() => setSliding(false)}
                >
                    {pages?.flatMap((p, i) => {
                        if (usePIs && p.positiveIndicator) return piPanel(p);
                        if (p.comment) return commentPanel(p);
                        return sliderPanel(p);
                    })}

                    {competency.review_Comment && (
                        <>
                            <div className="piStatement">{ratingComments(competency)}</div>
                            {inPreview && (
                                <div className="text-right">
                                    <button className="btn btn-primary btn-sm" onClick={next}>
                                        {SystemText('Reviews/Appraisals/ContinuePreview')}
                                        <i className="fa fa-chevron-right ml10" />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </PanelSlider>
            </div>
        </div>
    );
};

export default Rating;
